import React, { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Download, Printer } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import api, { apiError } from "@/lib/api";
import { money } from "@/lib/constants";
import { useLang } from "@/i18n";
import { Panel, Empty } from "@/pages/shared";

const iso = (d) => d.toISOString().slice(0, 10);
const monday = (d) => { const x = new Date(d); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x; };

export default function AdminSellerRevenue() {
  const { t } = useLang();
  const [view, setView] = useState("week");
  const [weekStart, setWeekStart] = useState(() => iso(monday(new Date())));
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);

  const range = useCallback(() => {
    if (view === "week") {
      const s = new Date(weekStart);
      const e = new Date(s); e.setDate(e.getDate() + 6);
      return [iso(s), iso(e)];
    }
    const [y, m] = month.split("-").map(Number);
    return [`${month}-01`, iso(new Date(Date.UTC(y, m, 0)))];
  }, [view, weekStart, month]);

  const load = useCallback(async () => {
    const [start, end] = range();
    try { setData((await api.get(`/revenue/by-seller?start=${start}&end=${end}`)).data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, [range]);
  useEffect(() => { load(); }, [load]);

  const shift = (days) => { const d = new Date(weekStart); d.setDate(d.getDate() + days); setWeekStart(iso(monday(d))); };

  const csv = () => {
    if (!data) return;
    const head = ["Medewerker", "Nummer", ...data.dates, "Contant", "Op rekening", "Facturen", "Totaal", "Verkopen"];
    const lines = data.rows.map((r) => [r.seller_name, r.seller_number,
      ...r.days.map((d) => (d.cash + d.account + d.invoices).toFixed(2)),
      r.cash.toFixed(2), r.account.toFixed(2), r.invoices.toFixed(2), r.total.toFixed(2), r.count]);
    const blob = new Blob([[head, ...lines].map((l) => l.join(";")).join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `omzet-medewerker-${view === "week" ? weekStart : month}.csv`;
    a.click();
  };

  const weekMode = view === "week";

  return (
    <Panel
      title={t("admin_seller_revenue")}
      testid="seller-revenue-panel"
      action={
        <div className="flex flex-wrap items-center gap-2 print:hidden">
          <div className="flex rounded-full border border-border overflow-hidden">
            {["week", "month"].map((v) => (
              <button key={v} onClick={() => setView(v)} data-testid={`seller-view-${v}`}
                className={`px-3 py-1.5 text-xs font-semibold transition-colors ${view === v ? "bg-secondary text-primary" : "text-muted-foreground hover:bg-muted"}`}>
                {t(v === "week" ? "hours_week" : "hours_month")}
              </button>
            ))}
          </div>
          {weekMode ? (
            <div className="flex items-center gap-1">
              <Button variant="outline" size="icon" onClick={() => shift(-7)} data-testid="seller-prev-week"><ChevronLeft className="h-4 w-4" /></Button>
              <Input type="date" value={weekStart} onChange={(e) => setWeekStart(iso(monday(new Date(e.target.value))))} className="w-40" data-testid="seller-week-input" />
              <Button variant="outline" size="icon" onClick={() => shift(7)} data-testid="seller-next-week"><ChevronRight className="h-4 w-4" /></Button>
            </div>
          ) : (
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40" data-testid="seller-month-input" />
          )}
          <Button variant="outline" onClick={csv} data-testid="seller-csv"><Download className="h-4 w-4 mr-1" />CSV</Button>
          <Button variant="outline" onClick={() => window.print()} data-testid="seller-print"><Printer className="h-4 w-4 mr-1" />{t("print")}</Button>
        </div>
      }
    >
      <div className="p-5 space-y-4">
        <p className="text-sm text-muted-foreground">{t("seller_revenue_desc")}</p>
        {!data || data.rows.length === 0 ? <Empty /> : (
          <div className="overflow-x-auto">
            <Table data-testid="seller-table">
              <TableHeader>
                <TableRow>
                  <TableHead>{t("seller")}</TableHead>
                  {weekMode && data.dates.map((d) => (
                    <TableHead key={d} className="text-right whitespace-nowrap">{d.slice(8)}/{d.slice(5, 7)}</TableHead>
                  ))}
                  <TableHead className="text-right">{t("pay_cash")}</TableHead>
                  <TableHead className="text-right">{t("pay_account")}</TableHead>
                  <TableHead className="text-right">{t("invoices")}</TableHead>
                  <TableHead className="text-right">{t("total")}</TableHead>
                  <TableHead className="text-right">{t("sales")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.rows.map((r) => (
                  <TableRow key={`${r.seller_name}-${r.seller_number}`} data-testid={`seller-row-${r.seller_number || r.seller_name}`}>
                    <TableCell className="font-medium whitespace-nowrap">
                      {r.seller_name}
                      {r.seller_number && <span className="block text-[11px] text-muted-foreground tabular-nums">{r.seller_number}</span>}
                    </TableCell>
                    {weekMode && r.days.map((d) => (
                      <TableCell key={d.date} className="text-right tabular-nums">
                        {d.cash + d.account + d.invoices ? money(d.cash + d.account + d.invoices) : "—"}
                      </TableCell>
                    ))}
                    <TableCell className="text-right tabular-nums text-emerald-600">{money(r.cash)}</TableCell>
                    <TableCell className="text-right tabular-nums text-sky-600">{money(r.account)}</TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">{money(r.invoices)}</TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">{money(r.total)}</TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">{r.count + r.invoice_count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </Panel>
  );
}
